/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { LeftOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import Text from 'src/components/ts-components/Text'
import Breadcrumb, {
  type BreadcrumbItem,
} from 'src/components/ts-components/Breadcrumb'

interface FullScreenModalHeaderProps {
  /** Already Title Cased by the caller (`capitalizeWords`). */
  title: string
  /**
   * Small uppercase label above the title, as on the profile header ("PLAYER
   * PROFILE" over the name). Already Title Cased by the caller.
   */
  caption?: string
  /**
   * Where the screen was opened from ("Org › Team"), in the page headers'
   * style, in the caption's place — it replaces `caption` when given.
   */
  breadcrumbs?: BreadcrumbItem[]
  /** The breadcrumb's home icon action (it shows only with breadcrumbs). */
  onHome?: () => void
  /** Renders a BACK link above the title when provided and no `breadcrumbs`. */
  onBack?: () => void
  /** The BACK link's text ("Back to Profile"). Defaults to "Back". */
  backLabel?: string
  /** Right-aligned header actions (`HeaderActionButton`s). */
  actions?: React.ReactNode
}

/** Title row of a `FullScreenModal`: optional BACK link, title, actions. */
const FullScreenModalHeader: React.FC<FullScreenModalHeaderProps> = ({
  title,
  caption,
  breadcrumbs,
  onHome,
  onBack,
  backLabel = 'Back',
  actions,
}) => (
  <div css={headerContainerStyle}>
    <div css={leftSideStyle}>
      {/* A breadcrumb already leads back, so BACK shows only without one. */}
      {onBack && !breadcrumbs?.length ? (
        <button type='button' css={backLinkStyle} onClick={onBack}>
          <LeftOutlined aria-hidden='true' />
          <Text
            variant='captionXSmall'
            weight='semiBold'
            color={colors.NEUTRAL_50}
            style='margin: 0; letter-spacing: 1.44px; text-transform: uppercase;'
          >
            {backLabel}
          </Text>
        </button>
      ) : null}
      {breadcrumbs?.length ? (
        <Breadcrumb items={breadcrumbs} variant='header' onHome={onHome} />
      ) : caption ? (
        <Text
          variant='paragraphXSmall'
          weight='semiBold'
          color={colors.NEUTRAL_50}
          style='margin: 0; text-transform: uppercase; letter-spacing: 1.44px;'
        >
          {caption}
        </Text>
      ) : null}
      <Text
        variant='displaySmall'
        weight='semiBold'
        color={colors.WHITE}
        style={`${
          isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
            : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'
        }`}
      >
        {title}
      </Text>
    </div>
    {actions ? <div css={rightSideStyle}>{actions}</div> : null}
  </div>
)

export default FullScreenModalHeader

// ---- styled ----
const headerContainerStyle = css`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding-bottom: 24px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
  margin-bottom: 24px;
`

const leftSideStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const backLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const rightSideStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`
